import React, { useState, useMemo, useRef, useEffect } from 'react';
import cn from 'classnames';
import { useDrag } from '@use-gesture/react';
import { isTouchDevice } from '../../util';

import Image from '../Image';

import * as styles from './PanableImage.module.css';

const PanableImage = ({
  image,
  containerHeight,
  containerWidth,
  // Allow panning for images with a ratio bigger than 4/3
  paningThreshold = 4 / 3,
  style,
  onClick
}) => {
  const isTouch = isTouchDevice();
  const panContainerRef = useRef(null);
  const [hasPanned, setHasPanned] = useState(false);

  const [isPanable, imageHeight, imageWidth, maxTranslateX] = useMemo(() => {
    const containerAspectRatio = containerWidth / containerHeight;
    const imageAspectRatio = image.width / image.height;

    const imageIsWiderThanContainer = imageAspectRatio > containerAspectRatio;
    const isPanable = imageAspectRatio >= paningThreshold;

    const imageHeight = isPanable
      ? containerHeight
      : imageIsWiderThanContainer
      ? (containerWidth / image.width) * image.height
      : containerHeight;

    const imageWidth =
      !isPanable && imageIsWiderThanContainer
        ? containerWidth
        : (containerHeight / image.height) * image.width;

    const maxTranslateX = isPanable ? -1 * (imageWidth - containerWidth) : 0;

    return [isPanable, imageHeight, imageWidth, maxTranslateX];
  }, [
    containerHeight,
    containerWidth,
    image.height,
    image.width,
    paningThreshold
  ]);

  // center the image
  useEffect(() => {
    setOffset(maxTranslateX / 2);
  }, [maxTranslateX]);

  const rootClassNames = cn(styles.root, {
    [styles.isTouch]: isTouch,
    [styles.panable]: isPanable,
    [styles.hasPanned]: hasPanned
  });

  const setOffset = offset => {
    if (!panContainerRef.current) return;
    panContainerRef.current.style.transform = `translateX(${offset}px)`;
  };

  const bind = useDrag(
    ({ offset: [x], direction: [dx], down }) => {
      if (!panContainerRef.current) return;
      if (dx) {
        setHasPanned(true);
        setOffset(x + maxTranslateX / 2);
      }
    },
    {
      axis: 'x',
      bounds: { left: maxTranslateX / 2, right: maxTranslateX / -2 }
    }
  );

  return (
    <div
      className={rootClassNames}
      style={{
        '--image-height': `${imageHeight}px`,
        '--image-width': `${imageWidth}px`,
        ...style
      }}
      onClick={!isPanable ? onClick : null}
      {...bind()}
    >
      <div ref={panContainerRef}>
        <Image image={image} className={cn(styles.image)} />
      </div>
      {isPanable && (
        <div className={cn(styles.panHint)} aria-hidden>
          &larr; Explora &rarr;
        </div>
      )}
    </div>
  );
};

export default PanableImage;
